// ⚠ 확장자 `.js` 는 **빼면 안 된다.** 이 패키지는 ESM 으로 빌드되므로 런타임이
//    상대 경로를 그대로 해석한다 — 확장자가 없으면 서버가 기동조차 못 한다
//    (`ERR_MODULE_NOT_FOUND: .../dist/enums`). 타입 검사와 단위 시험은 번들러가
//    해석해 주므로 **둘 다 초록인 채로 통과한다** — 실기동에서만 드러난다.
//    2026-08-28 에 실제로 겪었다. 이 폴더의 다른 파일 전부가 같은 관례를 쓴다.
import { UserRole } from './enums.js';
// 역할 집합의 **정본 한 벌**. 서버(@Roles · CASL)와 콘솔(버튼 · 라우트 · 사이드바)이
// 같은 상수를 import 한다 — 미러를 두 벌 두면 반드시 갈린다(그동안 실제로 갈렸다:
// "서버는 되는데 화면이 없음" · "버튼은 보이는데 저장만 403").
//
// ── 왜 통합했나 (오너 결정, 2026-08-27) ──────────────────────────────────
//   "사업팀, 제조팀, 필드팀 권한을 구분하지 않고 모든 등록과 삭제, 완전삭제는
//    오픈하겠음. 권한이 없어서 보지 못했던 탭도 오픈. 다만 로그로 누가 삭제하고
//    등록하고 수정했는지 명확하게 기록이 남을 수 있어야함.
//    // Admin 탭은 여전히 관리자만 볼 수 있도록."
//
// 종전엔 세 팀의 쓰기 권한이 자원별로 갈라져 있었다(영업=딜·계약·고객사·매장,
// 제조=로봇·배치·부품, 필드=현장보고·방문·첨부). 그래서 "필드팀이 계약을 못 고치고
// 영업팀이 로봇을 못 만드는" 칸막이가 곳곳에 있었고, 실제 업무는 그 경계를 넘나들어
// 매번 다른 팀을 거쳐야 했다. 오너가 그 구분을 없애기로 했다.
//
// **대가**: 권한 경계가 사라진 만큼 감사 장부가 유일한 안전망이 된다. 그래서 이
// 개방은 감사 보강(2026-08-28, docs/work_log/audit-hardening-2026-08-28)이 **먼저
// 끝난 뒤에** 이뤄졌다 — 순서를 바꾸면 안 된다.
//
// ⚠️ 회계·법적으로 되돌릴 수 없는 4종(로봇 소유 법인 이전 · 폐기 · 계약 해지/발효 ·
//    고객사/계약 완전 파기)도 **오너가 개방을 선택했다**(엔지니어링 권고는 관리자
//    유지였고, 오너가 그 권고를 듣고 뒤집었다). 여기 적어 두는 이유는 나중에
//    "왜 이게 열려 있지?"를 묻는 사람이 근거를 찾을 수 있게 하기 위해서다.
/**
 * **운영 권한** — 등록·수정·삭제·복구·완전삭제를 할 수 있는 역할 전부.
 *
 * VIEWER 만 빠진다(읽기 전용). 레거시 MANAGER 는 통합 후 세 팀 매니저와 같은
 * 집합이 되므로 그대로 포함한다 — 기존 계정이 갑자기 권한을 잃지 않는다.
 */
export const OPERATIONS_ROLES = [
    UserRole.ADMIN,
    UserRole.MANAGER,
    UserRole.SALES_MANAGER,
    UserRole.MFG_MANAGER,
    UserRole.FIELD_MANAGER,
];
/**
 * **관리자 전용** — Admin 탭 6항목(개인정보 요청 · 감사 로그 · 사용자 · 미입력
 * 인박스 · 옵션 그룹 · 셀프 체크 로그)과 시스템 운영 도구.
 *
 * 오너가 명시적으로 남긴 경계다. 업무 자원의 완전삭제와는 다른 축이다 —
 * 이쪽은 "회사 계정·개인정보·장부 자체"를 다루는 자리다.
 */
export const ADMIN_ONLY_ROLES = [UserRole.ADMIN];
/** 로그인한 누구나(읽기 전용 VIEWER 포함). 조회 전용 화면의 접근 집합. */
export const ALL_SIGNED_IN_ROLES = [...OPERATIONS_ROLES, UserRole.VIEWER];
/**
 * **사업팀 시트 화면**(인바운드 현황 · 도입 문의 현황 · 데모-계약 전환 — 구글 시트 거울) 열람.
 *
 * 제조팀(MFG_MANAGER)만 뺀다 — 오너 2026-10-23 "인바운드현황, 도입문의 현황, 데모-계약 전환은
 * 제조팀은 못보게 해야해". 위 '권한 통합'의 예외다. 레거시 MANAGER · VIEWER 는 그대로 둔다
 * (MANAGER 는 팀이 정해지지 않은 옛 계정이라 지금 막으면 사업팀 사람이 갑자기 못 볼 수 있다).
 * 라우트 · 사이드바 · 서버 GET 이 이 상수를 함께 쓴다.
 */
export const BUSINESS_SHEET_VIEW_ROLES = ALL_SIGNED_IN_ROLES.filter((r) => r !== UserRole.MFG_MANAGER);
/** 사업팀 시트 화면 고치기 · 새 줄 — 운영 권한에서 제조팀을 뺀 것(서버 PATCH · POST). */
export const BUSINESS_SHEET_EDIT_ROLES = OPERATIONS_ROLES.filter((r) => r !== UserRole.MFG_MANAGER);
/**
 * 이 역할이 업무 자원을 바꿀 수 있는가 — 버튼·라우트·서버 가드가 **같은 이 판정**을 쓴다.
 *
 * 자원별로 갈래를 두지 않는다. 갈래를 두는 순간 그 갈래가 화면과 서버 두 벌이 되고,
 * 한쪽만 고치는 사고가 난다(이 저장소가 실제로 겪은 사고의 원인).
 */
export function canOperate(role) {
    return OPERATIONS_ROLES.includes(role);
}
/** 관리자인가 — Admin 탭·시스템 도구의 유일한 판정. */
export function isAdminRole(role) {
    return role === UserRole.ADMIN;
}
//# sourceMappingURL=roles.js.map